import { DraftExportRequest, ResourceDraft } from '../../../api';
import { BinaryResponse } from '../../../core';
import { LinkedCredential } from '../../models';
import { verifiableCredentialDataSchema } from '../../schemas';
import { ApiClient, DraftDescriptor } from '../../types';
import { strictlyConvertToApiType } from '../../utils/converters';
import { generateKey } from '../../utils/idempotency';
import { DraftDataDescriptor, DraftMetadata } from '../types';
import { VerifiableCredential } from '../verifiable/verifiable';

export class DraftCredential {
    constructor(
        protected client: ApiClient,
        protected draftData: DraftDataDescriptor,
        public descriptor: DraftDescriptor,
    ) {}

    toLinkedCredential(): LinkedCredential<unknown> {
        return new LinkedCredential(this.client, `urn:draft:${this.descriptor.id}`);
    }

    async getMetaData(): Promise<DraftMetadata> {
        const { subject, validUntil } = this.draftData.meta || {};

        return {
            subject,
            validUntil,
        };
    }

    async export(params: DraftExportRequest): Promise<BinaryResponse> {
        return this.client.drafts.draftExport(this.descriptor.id, this.descriptor.revision, params);
    }

    async delete(): Promise<void> {
        await this.client.drafts.draftDelete(this.descriptor.id, {
            ifMatch: this.descriptor.etag,
            idempotencyKey: generateKey('delete'),
        });
    }

    async issue(privateKeyId: string): Promise<VerifiableCredential> {
        const { id, etag, revision, data } = await this.client.drafts.draftLatestIssue(this.descriptor.id, {
            keyId: privateKeyId,
        });

        return new VerifiableCredential(this.client, {
            id: id,
            etag: etag,
            revision: revision,
            data: strictlyConvertToApiType(verifiableCredentialDataSchema, {
                id: data.id,
                digestMultibase: data.digestMultibase,
            }),
        });
    }

    async toResource(): Promise<ResourceDraft> {
        return this.client.drafts.draftRevision(this.descriptor.id, this.descriptor.revision);
    }
}
